/*
  Nehir arama: yazım hatası düzeltmesi.
  Arama sonuç vermezse sorgudaki tanınmayan kelimeleri ürün başlığı/açıklaması sözlüğündeki en yakın kelimeyle
  değiştirir (ör. "tencre" -> "tencere", "tvaa" -> "tava").
  - Arama sayfası: sections/main-search.liquid [data-ts-fuzzy] işaretçisi -> düzeltilmiş aramaya yönlendirir (&oq=eski).
  - Öneri kutusu: snippets/predictive-search.liquid [data-ts-fuzzy-ps] işaretçisi -> düzeltilmiş sonuçları gösterir.
  Sözlük: templates/collection.search-vocab.liquid (/collections/all?view=search-vocab), 6 saat localStorage'da.
  Eşleşme kuralı Liquid tarafıyla aynı: başlıkta herhangi bir yerde ya da açıklamada kelime başında.
*/
(() => {
  const CACHE_KEY = 'tsVocab:v1';
  const CACHE_TTL = 6 * 60 * 60 * 1000;
  const VOCAB_URL = '/collections/all?view=search-vocab';

  const TR_MAP = { ı: 'i', ç: 'c', ş: 's', ğ: 'g', ö: 'o', ü: 'u', â: 'a', î: 'i', û: 'u' };
  const normalize = (text) =>
    String(text || '')
      .replace(/İ/g, 'i')
      .toLowerCase()
      .replace(/[ıçşğöüâîû]/g, (c) => TR_MAP[c])
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(Boolean);

  let vocabPromise;

  function loadVocab() {
    if (vocabPromise) return vocabPromise;
    vocabPromise = (async () => {
      try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        if (cached && Date.now() - cached.at < CACHE_TTL) return buildVocab(cached.pages);
      } catch (e) {}
      const first = await fetch(`${VOCAB_URL}&page=1`).then((r) => r.json());
      const rest = await Promise.all(
        Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) =>
          fetch(`${VOCAB_URL}&page=${i + 2}`).then((r) => r.json())
        )
      );
      const pages = [first, ...rest].map((p) => ({ t: p.t, d: p.d }));
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), pages }));
      } catch (e) {}
      return buildVocab(pages);
    })();
    vocabPromise.catch(() => (vocabPromise = null));
    return vocabPromise;
  }

  function buildVocab(pages) {
    const title = new Map(); // normalize kelime -> { count, surface }
    const desc = new Set();
    for (const page of pages) {
      for (const raw of page.t.split(/\s+/)) {
        const [word] = normalize(raw);
        if (!word || word.length < 3 || /\d/.test(word)) continue;
        const entry = title.get(word) || { count: 0, surface: null };
        entry.count += 1;
        const surface = raw.toLocaleLowerCase('tr-TR').replace(/[^a-zçğıöşüâîû0-9]/g, '');
        if (!entry.surface && normalize(surface)[0] === word) entry.surface = surface;
        title.set(word, entry);
      }
      for (const word of page.d.split(' ')) {
        if (word.length >= 3 && !/\d/.test(word) && !title.has(word)) desc.add(word);
      }
    }
    return { title, desc, titleWords: [...title.keys()], descWords: [...desc] };
  }

  // Liquid süzgeciyle aynı: başlık kelimesi içinde geçiyor ya da açıklama kelimesi bununla başlıyor
  function isKnown(vocab, word) {
    return vocab.titleWords.some((w) => w.includes(word)) || vocab.descWords.some((w) => w.startsWith(word));
  }

  // Optimal string alignment (yer değiştirme dahil Damerau-Levenshtein)
  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let prev2 = null;
    let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
      const cur = [i];
      let rowMin = i;
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (prev2 && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
        cur.push(v);
        rowMin = Math.min(rowMin, v);
      }
      if (rowMin > max) return max + 1;
      prev2 = prev;
      prev = cur;
    }
    return prev[b.length];
  }

  // Sıra: uzaklık, başlık kelimesi önce, aynı ilk harf, başlıklarda sık geçen
  function isBetter(a, b) {
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] < b[i];
    return false;
  }

  function correctWord(vocab, word) {
    if (word.length < 3 || /\d/.test(word) || isKnown(vocab, word)) return null;
    const max = word.length <= 4 ? 1 : 2;
    let best = null;
    const consider = (candidate, isTitle) => {
      const d = distance(word, candidate, max);
      if (d > max) return;
      const count = isTitle ? vocab.title.get(candidate).count : 0;
      const score = [d, isTitle ? 0 : 1, candidate[0] === word[0] ? 0 : 1, -count];
      if (!best || isBetter(score, best.score)) {
        best = { candidate, score, surface: isTitle ? vocab.title.get(candidate).surface : null };
      }
    };
    vocab.titleWords.forEach((w) => consider(w, true));
    vocab.descWords.forEach((w) => consider(w, false));
    return best && (best.surface || best.candidate);
  }

  // Düzeltilmiş sorgu ya da düzeltme yoksa null
  async function correctQuery(query) {
    const vocab = await loadVocab();
    const words = String(query).trim().split(/\s+/).filter(Boolean);
    let changed = false;
    const fixed = words.map((raw) => {
      const [word] = normalize(raw);
      const fix = word && normalize(raw).length === 1 ? correctWord(vocab, word) : null;
      if (fix) changed = true;
      return fix || raw;
    });
    return changed ? fixed.join(' ') : null;
  }

  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  const noteHtml = (from, to) =>
    `“${escapeHtml(from)}” için sonuç bulunamadı. “<strong>${escapeHtml(to)}</strong>” için sonuçlar gösteriliyor.`;

  // Arama sayfası
  async function handleSearchPage() {
    const params = new URLSearchParams(location.search);
    const note = document.querySelector('[data-ts-fuzzy-note]');
    if (params.get('oq') && note) {
      note.innerHTML = noteHtml(params.get('oq'), params.get('q') || '');
      note.classList.remove('hidden');
    }
    const marker = document.querySelector('[data-ts-fuzzy]');
    if (!marker || params.get('oq')) return;
    const fixed = await correctQuery(marker.dataset.tsFuzzy).catch(() => null);
    if (!fixed) return;
    params.set('oq', params.get('q') || marker.dataset.tsFuzzy);
    params.set('q', fixed);
    params.delete('page');
    location.replace(`${location.pathname}?${params}`);
  }

  // Öneri kutusu
  const handled = new WeakSet();
  async function handlePredictive(marker) {
    if (handled.has(marker)) return;
    handled.add(marker);
    const box = marker.closest('predictive-search');
    if (!box || typeof box.setupURL !== 'function') return;
    if (box.searchProductTypes && box.searchProductTypes.value) return;
    const term = marker.dataset.tsFuzzyPs;
    const fixed = await correctQuery(term).catch(() => null);
    if (!fixed || box.getQuery() !== term.trim()) return;
    const originalUrl = box.setupURL().toString();
    const url = box.setupURL();
    url.searchParams.set('q', fixed);
    const text = await fetch(url.toString()).then((r) => (r.ok ? r.text() : null)).catch(() => null);
    if (!text || box.getQuery() !== term.trim()) return;
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const results = doc.getElementById('PredictiveSearchResults-' + FoxTheme.utils.getSectionId(box));
    if (!results || results.querySelector('[data-ts-fuzzy-ps]')) return;
    results.insertAdjacentHTML('afterbegin', `<p class="ts-fuzzy-note text-subtext" style="margin:0 0 1.2rem">${noteHtml(term, fixed)}</p>`);
    const html = doc.documentElement.outerHTML;
    box.cachedMap.set(originalUrl, html);
    box.renderSearchResults(html);
  }

  function scan(root) {
    root.querySelectorAll && root.querySelectorAll('[data-ts-fuzzy-ps]').forEach(handlePredictive);
  }

  function init() {
    handleSearchPage();
    scan(document);
    new MutationObserver((records) => {
      for (const r of records) r.addedNodes.forEach((n) => n.nodeType === 1 && scan(n.parentNode || n));
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.tsFuzzy = { correctQuery, loadVocab };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
